import type { Student } from '@mathflat/domain/Student'
import { makeAutoObservable } from 'mobx'

import { SchoolGrade } from './SchoolGrade'

type Grade = Student['grade'] | 'ALL'
type FlatGrade = Student['grade'] | 7 | 8 | 9 | 10 | 11 | 12 | 'UNKNOWN'
type SchoolType = Student['schoolType'] | 'ALL'

type SchoolGradeFilterType = {
  grade: Grade
  flatGrade: FlatGrade
  schoolType: SchoolType | 'ALL'
}

type ConstructorParams = Partial<SchoolGradeFilterType>

// 필터에서는 통상적인 SchoolGrade와 다르게 적용 되어서 만듬 (ALL 추가)
export class SchoolGradeFilter {
  schoolType: SchoolGradeFilterType['schoolType']
  grade: Grade

  constructor({ schoolType = 'ELEMENTARY', grade = 1 }: ConstructorParams = {}) {
    makeAutoObservable(this)

    this.schoolType = schoolType
    this.grade = grade
  }

  get flatGrade(): FlatGrade {
    if (this.grade === 'ALL') return 'UNKNOWN'
    const elemGrade: 0 | 6 | 9 = (() => {
      switch (this.schoolType) {
        default:
        case 'ELEMENTARY':
          return 0
        case 'MIDDLE':
          return 6
        case 'HIGH':
          return 9
      }
    })()
    return (elemGrade + this.grade) as FlatGrade
  }

  set flatGrade(val: FlatGrade) {
    if (val === 'UNKNOWN') return

    if (val > 9) {
      this.schoolType = 'HIGH'
      this.grade = (val - 9) as Grade
      return
    }

    if (val > 6) {
      this.schoolType = 'MIDDLE'
      this.grade = (val - 6) as Grade
      return
    }

    this.schoolType = 'ELEMENTARY'
    this.grade = val as Grade
    return
  }

  get selectableGrade() {
    switch (this.schoolType) {
      case 'ELEMENTARY':
        return [1, 2, 3, 4, 5, 6]
      case 'HIGH':
      case 'MIDDLE':
      default:
        return [1, 2, 3]
    }
  }

  get koLabel() {
    if (this.flatGrade === 'UNKNOWN') return ''
    return SchoolGrade.KO_LABEL[this.flatGrade - 1]
  }

  get toJson() {
    return {
      grade: this.grade,
      schoolType: this.schoolType === 'ALL' ? null : this.schoolType,
    }
  }

  setSchoolType(schoolType: SchoolGradeFilterType['schoolType']) {
    this.schoolType = schoolType
  }

  setGrade(grade: Grade) {
    this.grade = grade
  }

  setSchoolGrade(schoolType: SchoolGradeFilterType['schoolType'], grade: Grade) {
    this.setGrade(grade)
    this.setSchoolType(schoolType)
  }
}
